import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "@/config";
import { AutoTuneSavingsTrendsResponseSchema } from "./types";
import type {
  AutoTuneHistoryResponse,
  AutoTuneSavingsTrendsResponse,
  AutoTuneStatusResponse,
} from "./types";

export const AUTO_TUNE_HISTORY_KEY = "jobs-auto-tune-history";
export const AUTO_TUNE_STATUS_KEY = "jobs-auto-tune-status";
export const AUTO_TUNE_SAVINGS_TRENDS_KEY = "jobs-auto-tune-savings-trends";

const baseUrl = (jobId: string, segment: string) =>
  `${API_URL}/api/v1/databricks/jobs/${jobId}/auto-tune/${segment}`;

const okOrEmpty = async <T>(promise: Promise<unknown>, fallback: T): Promise<T> => {
  try {
    const data = (await promise) as unknown as T;
    return data ?? fallback;
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return fallback;
    }
    throw e;
  }
};

export const useAutoTuneStatus = (
  jobId: string,
  instanceId: string | number | undefined,
  enabled = true,
  workspaceId?: string
) =>
  useQuery({
    queryKey: [AUTO_TUNE_STATUS_KEY, jobId, instanceId, workspaceId] as const,
    queryFn: () =>
      okOrEmpty<AutoTuneStatusResponse>(
        axios.get(baseUrl(jobId, "status"), {
          params: {
            instance_id: instanceId,
            ...(workspaceId ? { workspace_id: workspaceId } : {}),
          },
        }),
        {
          auto_tune_mode: null,
          preflight: { blocked: false },
          recommendation_count: 0,
          tasks_enabled: 0,
          tasks_total: 0,
        }
      ),
    enabled: enabled && Boolean(jobId) && instanceId !== undefined && instanceId !== "",
    retry: false,
  });

interface HistoryParams {
  instance_id?: string | number;
  workspace_id?: string;
  // BE expects ``page`` / ``page_size``; the snake_case names match the
  // FastAPI Query() defaults in jobs_api.py::get_job_auto_tune_history.
  page?: number;
  page_size?: number;
  from?: string;
  to?: string;
}

export const useAutoTuneHistory = (jobId: string, params: HistoryParams, enabled = true) =>
  useQuery({
    queryKey: [AUTO_TUNE_HISTORY_KEY, jobId, params] as const,
    queryFn: () =>
      okOrEmpty<AutoTuneHistoryResponse>(axios.get(baseUrl(jobId, "history"), { params }), {
        items: [],
        total_count: 0,
        page: 1,
        page_size: 10,
        total_pages: 1,
      }),
    enabled: enabled && Boolean(jobId),
    retry: false,
  });

// Per-day realized + potential auto-tune savings for the job, scoped to
// the page-level calendar filter (the BE filters on the run window
// ``period_start_time``). Returns empty series on 404 so the charts show
// an empty state rather than erroring on jobs without savings rows yet.
export const useAutoTuneSavingsTrends = (
  jobId: string,
  instanceId: string | number | undefined,
  startDate: string,
  endDate: string,
  enabled = true
) =>
  useQuery({
    queryKey: [AUTO_TUNE_SAVINGS_TRENDS_KEY, jobId, instanceId, startDate, endDate] as const,
    queryFn: async () =>
      AutoTuneSavingsTrendsResponseSchema.parse(
        await okOrEmpty<AutoTuneSavingsTrendsResponse>(
          axios.get(baseUrl(jobId, "savings-trends"), {
            params: {
              instance_id: instanceId,
              start_date: startDate,
              end_date: endDate,
            },
          }),
          { actual: [], potential: [], granularity: "day" }
        )
      ),
    enabled:
      enabled &&
      Boolean(jobId) &&
      instanceId !== undefined &&
      instanceId !== "" &&
      Boolean(startDate),
    retry: false,
  });
